import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth/AuthProvider';
import { Provider as JotaiProvider } from 'jotai';
import { Login as LoginV0 } from './pages/v0/Login';
import { Login as LoginV3 } from './pages/v3/Login';
import { LoginRouter } from './pages/LoginRouter';
import { SharedProjectRouter } from './pages/SharedProjectRouter';
import { useEffect, useState, Suspense } from 'react';
import { supabase } from './lib/supabase';
import { initializeSystemTheme } from './utils/themeUtils';
import chakraTheme from './lib/chakraTheme';
import { trackEvent, identifyUser } from './lib/analytics';
import Shake from '@shakebugs/browser';
import { AppV0 } from './AppV0';
// import { AppV1 } from './AppV1';
import { AppV3 } from './pages/v3';
import { AssistiveTouch } from './components/AssistiveTouch';
import { DebugPanel } from './pages/v3/components/debug/debugPanel';
import { SplashScreen } from './components/v0/SplashScreen';
import { ReminderService } from './services/reminderService';
import { logger } from './lib/logger';
import { getPlatformInfo, isPWAMode } from './utils/platformUtils';


import { getBackendApiUrl, currentRegionAtom } from './atoms/shared/regionAtom';
import { useAtomValue, useAtom } from 'jotai';
import { fetchSelectedCharacterAtom, syncSelectedCharacterAtom } from './atoms/v3/characterAtoms';
import ErrorBoundary from './components/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { ErrorBoundary as HighlightErrorBoundary } from '@highlight-run/react';
import RootErrorBoundaryFallback from './components/RootErrorBoundaryFallback';
import ContentErrorBoundaryFallback from './components/ContentErrorBoundaryFallback';
import V3RouteHandler from './components/V3RouteHandler';

const AuthCallback = () => {
  const { user, loading } = useAuth();
  const [authProcessed, setAuthProcessed] = useState(false);

  useEffect(() => {
    // 处理 URL 参数中的访问令牌和刷新令牌
    const handleAuthCallback = async () => {
      const hash = window.location.hash;
      console.log('[AuthCallback] Processing auth callback, hash:', hash ? 'exists' : 'none');

      // 尝试从 URL 获取提供商信息
      const urlParams = new URLSearchParams(window.location.search);
      const provider = urlParams.get('provider') ||
                      (hash && hash.includes('provider=') ?
                        new URLSearchParams(hash.substring(1)).get('provider') :
                        'unknown');



      console.log('[AuthCallback] Auth provider:', provider);

      // 获取当前会话
      const { data } = await supabase.auth.getSession();
      console.log('[AuthCallback] Current session:', data.session ? 'exists' : 'none');

      // 如果已经有会话，记录登录成功事件
      if (data.session && data.session.user) {
        const currentUser = data.session.user;

        // 记录社交登录成功事件
        await trackEvent('user logged in', {
          method: provider || 'social',
          user_id: currentUser.id
        });

        // 识别用户
        await identifyUser(currentUser.id, {
          email: currentUser.email,
          auth_provider: provider || 'social'
        });
        Shake.registerUser(currentUser.id);

        console.log('[AuthCallback] Tracked social login success for user:', currentUser.id);
      }

      // 如果已经有会话或正在加载，不执行操作
      if (data.session || loading) {
        setAuthProcessed(true);
        return;
      }

      try {
        // 尝试从 URL 设置会话
        const { error, data: userData } = await supabase.auth.getUser();
        if (error) {
          console.error('[AuthCallback] Error getting user:', error.message);

          // 记录社交登录失败事件
          await trackEvent('login failed', {
            method: provider || 'social',
            error: error.message
          });
        } else if (userData.user) {
          // 记录社交登录成功事件
          await trackEvent('user logged in', {
            method: provider || 'social',
            user_id: userData.user.id
          });

          // 识别用户
          await identifyUser(userData.user.id, {
            email: userData.user.email,
            auth_provider: provider || 'social'
          });
          Shake.registerUser(userData.user.id);
        }
      } catch (err) {
        console.error('[AuthCallback] Error processing callback:', err);

        // 记录社交登录异常事件
        await trackEvent('login error', {
          method: provider || 'social',
          error: err instanceof Error ? err.message : String(err)
        });
      } finally {
        setAuthProcessed(true);
      }
    };

    if (!authProcessed) {
      handleAuthCallback();
    }
  }, [loading, authProcessed]);

  // 根据认证状态进行重定向
  if (loading || !authProcessed) {
    return <div className="w-full h-screen flex items-center justify-center">登录处理中...</div>;
  }

  if (user) {
    console.log('[AuthCallback] User authenticated, redirecting to root');
    return <Navigate to="/" replace />;
  }

  console.log('[AuthCallback] No user found, redirecting to /login');
  return <Navigate to="/login" replace />;
};

function App() {
  // 状态管理
  const [splashScreenVisible, setSplashScreenVisible] = useState(isPWAMode());
  const currentRegion = useAtomValue(currentRegionAtom);
  const [, fetchSelectedCharacter] = useAtom(fetchSelectedCharacterAtom);
  const [, syncSelectedCharacter] = useAtom(syncSelectedCharacterAtom);



  // 初始化系统主题和通知服务
  useEffect(() => {
    // 获取平台信息
    const platformInfo = getPlatformInfo();

    // 记录前端启动日志
    logger.info('Frontend App started', {
      platform: platformInfo.isNative ? platformInfo.os : 'web',
      isPwa: platformInfo.isPWA,
      userAgent: platformInfo.userAgent,
      platformInfo // 记录完整的平台信息
    });

    // 在开发环境下输出当前使用的后端API地址
    if (import.meta.env.DEV) {

      // 使用与apiClient.ts相同的逻辑来确定实际使用的API URL
      const actualBackendUrl = (() => {
        // 直接根据地理位置选择后端API，无视环境变量
        // 因为CDN策略是一处部署全球使用，环境变量不可依赖
        const regionBasedUrl = getBackendApiUrl();
        if (regionBasedUrl) {
          return regionBasedUrl;
        }
        // 最后的fallback到全球版本
        return 'https://tempo-0y6p.onrender.com/';
      })();

      const isUsingLocalBackend = actualBackendUrl?.includes('localhost');
      const isUsingEnvOverride = import.meta.env.VITE_BACKEND_API_URL && import.meta.env.VITE_BACKEND_API_URL !== 'https://your-backend-api-url/';

      console.log(`\n🌐 [Backend API] Current backend URL: ${actualBackendUrl}`);
      console.log(`📍 [Backend API] Detected region: ${currentRegion}`);
      console.log(`⚙️  [Backend API] Source: ${isUsingLocalBackend ? 'Local Development Server' : isUsingEnvOverride ? 'Environment Variable Override' : 'Geographic Selection'}`);
      console.log(`🖥️  [Platform] ${platformInfo.isNative ? `Native (${platformInfo.os})` : platformInfo.isElectron ? 'Electron' : platformInfo.isPWA ? 'PWA' : 'Web Browser'}`);
      console.log(`🔄 [Domain Switch] Auto-redirect enabled in app-startup-initializer.js\n`);
    }

    // 初始化系统主题
    initializeSystemTheme();

    // 初始化通知服务
    const initNotifications = async () => {
      try {
        const success = await ReminderService.initialize();
        if (success) {
          console.log('[App] Notification service initialized successfully');
        } else {
          console.warn('[App] Failed to initialize notification service');
        }
      } catch (error) {
        console.error('[App] Error initializing notification service:', error);
      }
    };

    // 只在原生平台上初始化通知服务
    if (platformInfo.isNative) {
      initNotifications();
    }

    // 域名自动跳转现在由 app-startup-initializer.js 处理
    console.log('[App] Domain auto-redirect is handled by app-startup-initializer.js');

    // 预加载默认角色数据，确保即使用户未登录也有默认角色可用
    const initializeDefaultCharacter = async () => {
      try {
        console.log('[App] Initializing default character data');
        await syncSelectedCharacter();
        console.log('[App] Default character initialized successfully');
      } catch (error) {
        console.warn('[App] Failed to initialize default character:', error);
        // 默认角色加载失败不应该影响应用启动
      }
    };

    // 延迟执行角色初始化，避免阻塞应用启动
    setTimeout(initializeDefaultCharacter, 100);
  }, [syncSelectedCharacter]);

  // 处理 Splash Screen 结束
  const handleSplashScreenFinish = () => {
    setSplashScreenVisible(false);
  };



  return (
    <Suspense fallback={<SplashScreen />}>
      <HighlightErrorBoundary>
        <ChakraProvider theme={chakraTheme}>
          <JotaiProvider>
            <AuthProvider>
              {/* Root Error Boundary */}
              <ErrorBoundary fallback={<RootErrorBoundaryFallback />}>
                {splashScreenVisible && (
                  <SplashScreen
                    minDisplayTime={2000}
                    fadeOutTime={500}
                    onFinish={handleSplashScreenFinish}
                  />
                )}

                {/* Content Error Boundary */}
                <ErrorBoundary fallback={<ContentErrorBoundaryFallback />}>
                  <Router>
                    {/* AssistiveTouch: Now inside Router */}
                    <AssistiveTouch />

                    {/* MobileDebugPanel: Global debug panel for development */}
                    <DebugPanel />

                    {/* V3 路由处理器：监听路由变化，设置 v3-enabled 类 */}
                    <V3RouteHandler />

                    <Routes>
                      <Route path="/login" element={<LoginRouter />} />
                      <Route path="/v0/login" element={<LoginV0 />} />
                      <Route path="/v3/login" element={<LoginV3 />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      {/* 项目分享路由 */}
                      <Route path="/s/project/:shareId" element={<SharedProjectRouter />} />
                      <Route path="/v0/*" element={<AppV0 />} />
                      {/* <Route path="/v1/*" element={<AppV1 />} /> */}
                      <Route path="/v3/*" element={<AppV3 />} />
                      <Route
                        path="/"
                        element={<Navigate to="/v3/chat" replace />}
                      />
                      {/* 移除通配符路由，避免捕获其他版本的路由 */}
                    </Routes>

                    {/* Analytics and SpeedInsights: Now inside Router */}
                    {import.meta.env.PROD && <Analytics />}
                    {import.meta.env.PROD && <SpeedInsights />}
                  </Router>

                  {/* 域名自动跳转现在由 app-startup-initializer.js 处理，无需弹窗 */}
                </ErrorBoundary>
              </ErrorBoundary> {/* End of Root Error Boundary */}
            </AuthProvider>
          </JotaiProvider>
        </ChakraProvider>
      </HighlightErrorBoundary>
    </Suspense>
  );
}

export default App;
